// src/redux/slices/drawers/controlled_substance_logging/containerEntryDrawerSlice.ts

import { Dayjs } from "dayjs";
import { csl } from "lobbie";
import { generateUUID, notify } from "src/utils";

import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { RootState } from "src/redux/store";
import { ESearchTableType } from "src/types/global_enums";
import { triggerSearchTableRefetch } from "../../searchTableRefetchSlice";
import { createDrawerSlice, IBaseDrawerState } from "../createDrawerSlice";
import { createDrawerActionMeta, createDrawerThunkMeta } from "../stackableDrawerConstant";

import {
    createSignaturePayloads,
    createStartSignatureRequestThunk,
    createSubmitSignatureRequestThunk,
    initialPrimarySignatureState,
    IPrimarySignatureState,
    processSignatureData,
    submitBatchSignatures,
} from "./signatureRequestService";

export interface IContainerEntryDrawerState extends IBaseDrawerState, IPrimarySignatureState {
    show: boolean;
    isSubmitting: boolean;
    medicationId?: number;
    ndcVariant?: csl.INdcVariantFullDetails;
    locationId?: number;
    existingContainerEntryLogs?: csl.IContainerLogFullDetails[];
    isLoading: boolean;
    error: string | null;
    lotNumber?: string;
    containerEntryTableRefreshTrigger: number;
}

export const initialStateContainerEntryDrawer: IContainerEntryDrawerState = {
    ...initialPrimarySignatureState,
    drawerHistoryInstanceId: "containerEntryDrawer",
    show: false,
    isSubmitting: false,
    medicationId: undefined,
    ndcVariant: undefined,
    locationId: undefined,
    existingContainerEntryLogs: undefined,
    isLoading: false,
    error: null,
    lotNumber: undefined,
    containerEntryTableRefreshTrigger: 0,
};

export const startSignatureRequestTask = createStartSignatureRequestThunk(
    "containerEntryDrawer/StartSignatureRequestTask",
);

export const submitSignatureRequestTask = createSubmitSignatureRequestThunk(
    "containerEntryDrawer/submitSignatureRequestTask",
    "containerEntryDrawer",
    [ESearchTableType.CSL_CURRENT_INVENTORY, ESearchTableType.CSL_CONTAINER_LOGS],
);

// Should probably have some shared structures here, or map in the form to this structure
interface ContainerEntrySubmitPayload {
    dateTimeReceivedISO: string;
    containers?: {
        containerNumber?: string;
        lotNumber?: string;
        containerSize?: number;
        expirationDate?: string;
    }[];
}

export const submitContainerEntryWithSigning = createAsyncThunk<
    void,
    ContainerEntrySubmitPayload,
    { state: { containerEntryDrawer: IContainerEntryDrawerState }; rejectValue: string }
>(
    "containerEntryDrawer/submitContainerEntryWithSigning",
    async (formData, { getState, dispatch, rejectWithValue }) => {
        const state = getState().containerEntryDrawer;
        const {
            ndcVariant,
            locationId,
            primarySignee,
            primarySignatureBlob,
            primarySignedDateTimeISO,
        } = state;

        if (!ndcVariant || !locationId || !primarySignee) {
            return rejectWithValue("Missing required data");
        }

        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/enter`;
            const response = await axios.post<csl.IContainerEntryResponse>(
                route,
                {
                    ndcVariantId: ndcVariant.id,
                    locationId,
                    dateTimeReceivedISO: formData.dateTimeReceivedISO,
                    containers: formData.containers,
                    primarySignerId: primarySignee.id,
                    primarySignerSignedDateTimeISO: primarySignedDateTimeISO,
                },
                { withCredentials: true },
            );
            const { signatureRequests } = response.data;
            const signaturePayloads = createSignaturePayloads(signatureRequests, state);
            await submitBatchSignatures(signaturePayloads, {
                successMessage: "Container entry created successfully",
                showSuccessNotification: true,
            });
            // Trigger refetch regardless of whether signatures were submitted
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
        } catch (error: any) {
            return rejectWithValue("Failed to submit container entry");
        }
    },
    createDrawerThunkMeta("hide"),
);

export const refreshContainerEntryDrawerState = createAsyncThunk<void, void, { state: RootState }>(
    "containerEntryDrawer/refreshState",
    async (_, { getState, dispatch }) => {
        const state = getState().containerEntryDrawer;
        // If drawer is not shown or submitting, don't refresh
        if (!state.show || state.isSubmitting) {
            return;
        }
        if (state.isSignatureMode && state.primarySignatureRequestId) {
            await dispatch(startSignatureRequestTask(state.primarySignatureRequestId)).unwrap();
        }
    },
);

export const startContainerEntry = createAsyncThunk<
    {
        ndcVariant?: csl.INdcVariantFullDetails;
        lotNumber?: string;
        locationId: number;
        drawerHistoryInstanceId: string;
    },
    {
        ndcVariant?: csl.INdcVariantFullDetails;
        lotNumber?: string;
    },
    { state: RootState; rejectValue: string }
>(
    "containerEntryDrawer/startContainerEntry",
    (params, { getState, rejectWithValue }) => {
        try {
            // if the ndcVariant is not provided, we will use the locationId from the state
            const state = getState();
            const currentLocationId =
                params?.ndcVariant?.medication?.locationId ??
                state.location.locationId ??
                state.location?.locations?.[0]?.id;
            if (!currentLocationId) {
                return rejectWithValue("No location ID available");
            }
            return {
                drawerHistoryInstanceId: generateUUID(),
                locationId: currentLocationId,
                ndcVariant: params.ndcVariant,
                lotNumber: params.lotNumber,
            };
        } catch (error: any) {
            return rejectWithValue("Failed to start container entry drawer");
        }
    },
    createDrawerThunkMeta("show"),
);

const containerEntryDrawerSlice = createDrawerSlice({
    name: "containerEntryDrawer",
    initialState: initialStateContainerEntryDrawer,
    reducers: {
        hideContainerEntryDrawer: {
            prepare: () => ({
                payload: undefined,
                meta: createDrawerActionMeta("hide"),
            }),
            reducer: (state) => {
                state.show = false;
            },
        },
        setContainerLocationId: (state, action: PayloadAction<number | undefined>) => {
            state.locationId = action.payload;
            // Reset medication and NDC variant when location changes
            state.medicationId = undefined;
            state.ndcVariant = undefined;
        },
        setContainerMedicationId: (state, action: PayloadAction<number | undefined>) => {
            state.medicationId = action.payload;
        },
        setContainerNdcVariant: (
            state,
            action: PayloadAction<csl.INdcVariantFullDetails | undefined>,
        ) => {
            state.ndcVariant = action.payload;
        },
        setPrimarySignee: (
            state,
            action: PayloadAction<csl.ISignatureRequestSigner | undefined>,
        ) => {
            state.primarySignee = action.payload;
            state.primarySignedDateTimeISO = undefined;
            state.primarySignatureBlob = undefined;
        },
        setPrimarySignedDate: (state, action: PayloadAction<Dayjs | undefined>) => {
            state.primarySignedDateTimeISO = action.payload?.toISOString();
        },
        setPrimarySignatureBlob: (state, action: PayloadAction<string | undefined>) => {
            state.primarySignatureBlob = action.payload;
        },
        setContainerLotNumber: (state, action: PayloadAction<string | undefined>) => {
            state.lotNumber = action.payload;
        },
        refreshContainerEntryDrawerState: (state) => {
            state.containerEntryTableRefreshTrigger += 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitSignatureRequestTask.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(submitSignatureRequestTask.fulfilled, (state) => {
                state.show = false; // Only closing drawer not resetting state because drawer middleware will handle that
            })
            .addCase(submitSignatureRequestTask.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = "Failed to submit signatures";
            })
            // Submitting a new container entry
            .addCase(submitContainerEntryWithSigning.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(submitContainerEntryWithSigning.fulfilled, (state) => {
                state.show = false; // Only closing drawer not resetting state because drawer middleware will handle that
            })
            .addCase(submitContainerEntryWithSigning.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to submit container entry";
            })
            // Opening up the Container Entry Drawer to sign an existing container entry
            .addCase(startSignatureRequestTask.pending, (state) => {
                return {
                    ...initialStateContainerEntryDrawer,
                    isLoading: true,
                    error: null,
                    show: true,
                    drawerHistoryInstanceId: `containerEntryDrawer-${generateUUID()}`,
                };
            })
            .addCase(startSignatureRequestTask.fulfilled, (state, action) => {
                processSignatureData(state, action.payload);
                // Set container entry specific state
                state.existingContainerEntryLogs = action.payload.containerLogs;
                state.isLoading = false;
                state.show = true;
                const firstEntry = action.payload.containerLogs[0];
                state.locationId = firstEntry.medication.locationId;
                state.medicationId = firstEntry.medication.id;
                state.ndcVariant = firstEntry.ndcVariant;
            })
            .addCase(startSignatureRequestTask.rejected, (state, action) => {
                state.isLoading = false;
                state.error = "Failed to load signature request";
            })
            .addCase(refreshContainerEntryDrawerState.fulfilled, (state) => {
                state.containerEntryTableRefreshTrigger += 1;
            })
            .addCase(startContainerEntry.fulfilled, (state, action) => {
                return {
                    ...initialStateContainerEntryDrawer,
                    drawerHistoryInstanceId: action.payload.drawerHistoryInstanceId,
                    locationId: action.payload.locationId,
                    ndcVariant: action.payload.ndcVariant,
                    lotNumber: action.payload.lotNumber,
                    show: true,
                };
            })
            .addCase(startContainerEntry.rejected, (state, action) => {
                state.error = action.payload || "Failed to start container entry";
                notify({
                    level: "error",
                    message: state.error,
                    title: "Error",
                });
            });
    },
});

export const {
    setContainerLocationId,
    setContainerMedicationId,
    setContainerNdcVariant,
    hideContainerEntryDrawer,
    setPrimarySignee,
    setPrimarySignedDate,
    setPrimarySignatureBlob,
    setContainerLotNumber,
} = containerEntryDrawerSlice.actions;

export default containerEntryDrawerSlice.reducer;
